import Bullet from '../components/Bullet';
import ReactNativeIcon from '../assets/icons/react-native.png';
import MuiIcon from '../assets/icons/mui.png';
import NextJsIcon from '../assets/icons/next.png';
import ReactQueryIcon from '../assets/icons/react-query.png';
import NodeJsIcon from '../assets/icons/node.png';
import NestJsIcon from '../assets/icons/nest.png';
import TypeScriptIcon from '../assets/icons/ts.png';
import MongoIcon from '../assets/icons/mongo.png';
// import JavaScriptIcon from '../assets/icons/js.png';
// import ReactIcon from '../assets/icons/react.png';
import '../css/project.css';

const projects = [
  {
    id: '01',
    title: 'Quality Track',
    date: 'Feb 2024',
    overview: (
      <>
        <main className='color'>
          <section>
            <p>
              <span className='tooltip bold'>
                Quality Track
                <span className='tooltiptext'>
                  Original name has been changed to maintain confidentiality.
                </span>
              </span>{' '}
              is a tailor-made enterprise application developed for a leading
              Indian manufacturer of{' '}
              <strong className='reveal'>Winding Wires and Conductors</strong>,
              operating in the{' '}
              <strong className='reveal'>
                high-precision industrial sector.
              </strong>
            </p>
          </section>

          <section>
            <p>
              The system was designed to overhaul their
              <strong className='reveal wavy-yellow'>
                {' '}
                Quality Assurance workflows,{' '}
              </strong>
              replacing traditional manual and spreadsheet-based methods with a
              modern, digital-first platform. It centralizes and streamlines
              quality checks, alerts, inspections, certifications, and
              corrective actions,{' '}
              <strong className='reveal'>
                significantly improving compliance in an{' '}
                <span className='bold highlight'>efficient manner.</span>
              </strong>
            </p>
          </section>
        </main>
      </>
    ),
    sections: (
      <>
        <section id='my-role'>
          <h2>My Role</h2>
          <p>
            I worked as a <strong className='reveal'>Frontend Developer</strong>{' '}
            on this project and contributed to UI planning and integration.{' '}
            <strong className='reveal'>
              My core responsibilities included:
            </strong>
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Developing key modules like NC Tracking, 8D Root Cause Analysis,
              Dashboard & MIS Reports, and the complete Settings Module.
            </li>
            <li>
              <Bullet />
              Designing reusable components, modals, and layouts to maintain
              consistency across modules.
            </li>
            <li>
              <Bullet />
              Building a custom Axios helper for managing API requests and error
              handling, integrated with React Query for data fetching and
              caching.
            </li>
            <li>
              <Bullet />
              Implementing form workflows using react hook form and data tables
              using DataGrid.
            </li>
            <li>
              <Bullet />
              Handling edge cases like API failure states, empty datasets, and
              conditional input validation.
            </li>
            <li>
              <Bullet />
              Collaborating daily with the backend team to refine requirements
              and resolve integration issues.
            </li>
          </ul>
        </section>

        <section id='problem'>
          <h2>The Problem</h2>
          <p>
            The client relied on{' '}
            <span className='reveal'>manual workflows</span> and{' '}
            <strong>spreadsheet-based tools</strong> to manage quality processes
            like inspections, test results, and compliance checks. This led to:
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              High risk of <strong className='reveal'>manual errors</strong> and
              data loss
            </li>
            <li>
              <Bullet />
              <span className='reveal'>Slow decision-making</span> and scattered
              records
            </li>
            <li>
              <Bullet />
              <strong className='reveal'>Limited traceability</strong> and poor
              audit readiness
            </li>
            <li>
              <Bullet />
              Difficulty in maintaining{' '}
              <span className='reveal'>compliance</span> across multiple
              stakeholders
            </li>
          </ul>
        </section>

        <section id='solution'>
          <h2>The Solution</h2>
          <p>
            We developed a custom enterprise-grade solution to digitize and
            centralize quality assurance operations.
          </p>
          <p>The application provides:</p>
          <ul className='list'>
            <li>
              <Bullet />
              Automatic fetching of Technical Data Sheets (TDS) for real-time
              inspection reference
            </li>
            <li>
              <Bullet />
              Integrated Inspection Sheet with auto-calculation of OK/Not OK
              status
            </li>
            <li>
              <Bullet />
              Instant generation of Test Certificates post inspection
            </li>
            <li>
              <Bullet />
              Workflow-driven creation of NCs and 8D Reports for corrective
              action
            </li>
            <li>
              <Bullet />
              Dashboards and role-based access to ensure compliance and data
              security
            </li>
          </ul>
        </section>

        <section id='my-work'>
          <h2>Modules I Developed</h2>
          <p>
            I was responsible for implementing several high-impact modules
            within the system:
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              NC Tracking: Real-time logging of non-conformities with
              validation, traceability, and review flow.
            </li>
            <div className='flexbox mb-20'>
              <img
                src='/assets/projects/quality-track/NC.png'
                width={'100%'}
                className='mt-4'
                alt='NC Flowchart'
              />
              <p className='underline mb-10'>
                Fig. 1 - NC flowchart illustration
              </p>
            </div>
            <li>
              <Bullet />
              8D Reports: Step-wise RCA forms with PDF export, role-based
              collaboration, and history tracking.
            </li>
            <div className='flexbox mb-20'>
              <img
                src='/assets/projects/quality-track/8D.png'
                width={'70%'}
                className='mt-4'
                alt='8D Flowchart'
              />
              <p className='underline mb-10'>Fig. 2 - 8D illustration</p>
            </div>
            <li>
              <Bullet />
              Dashboard & MIS: Real-time KPIs, tabular reports using DataGrid,
              exportable Excel & PDF files.
            </li>
            <li>
              <Bullet />
              Settings Module: Developed interfaces for user roles, unit info,
              pricing charts, notifications, and more using reusable components
              and modal-based forms.
            </li>
          </ul>
        </section>

        <section id='impact'>
          <h2 className='color font-size-title'>Impact</h2>
          <p>The solution delivered measurable operational improvements:</p>
          <ul className='list'>
            <li>
              <Bullet />
              95% reduction in manual errors during inspections.
            </li>
            <li>
              <Bullet />
              30% faster data entry and report generation.
            </li>
            <li>
              <Bullet />
              40% improvement in compliance tracking and audits.
            </li>
            <li>
              <Bullet />
              Audit readiness enhanced with PDF/Excel exports and full
              traceability.
            </li>
            <li>
              <Bullet />
              Overall 35% boost in operational efficiency across quality teams.
            </li>
          </ul>
        </section>
      </>
    ),
    images: [
      '/assets/projects/quality-track/1.png',
      '/assets/projects/quality-track/2.png',
      '/assets/projects/quality-track/3.png',
      '/assets/projects/quality-track/4.png',
      '/assets/projects/quality-track/5.png',
    ],
    techStack: [
      { src: NextJsIcon, alt: 'Next JS' },
      { src: ReactQueryIcon, alt: 'React Query' },
      { src: NestJsIcon, alt: 'Nest JS' },
      { src: TypeScriptIcon, alt: 'Type script' },
      { src: MongoIcon, alt: 'Mongo DB' },
    ],
  },
  {
    id: '02',
    title: 'Dynamic Diet',
    date: '12th March 2024',
    overview: (
      <>
        <main className='color'>
          <section>
            <p>
              Dynamic Diet CRM is a{' '}
              <span className='bold reveal'>
                customized customer relationship management system
              </span>{' '}
              built for a growing diet consultancy organization handling over{' '}
              <span className='bold reveal'>1000+ active clients.</span> The
              platform streamlines core operations like client{' '}
              <span className='bold reveal wavy-yellow'>
                tracking, order management, analytics, diet planning, and
                communication,
              </span>{' '}
              enabling the business to deliver personalized health and nutrition
              guidance at <span className='bold highlight'>scale.</span>
            </p>
          </section>

          {/* <section>
            <h2>Challenges in Traditional Diet Planning:</h2>
            <p className='mb-10'>
              Traditionally, diet specialists relied on{' '}
              <span className='bold'>
                manual tracking, spreadsheets, and handwritten meal plans
              </span>
              , leading to{' '}
              <strong className='wavy-yellow reveal'>inefficiencies</strong>,{' '}
              <strong className='wavy-yellow reveal'>
                lack of personalization
              </strong>{' '}
              and{' '}
              <strong className='wavy-yellow reveal'>missed follow-ups</strong>,
              . Diet Master 360 CRM{' '}
              <span className='bold highlight'>automates and organizes</span>{' '}
              the process by providing a{' '}
              <span className='bold'>
                structured, centralized, and data-driven approach{' '}
              </span>
              to client health management.
            </p>
          </section> */}
        </main>
      </>
    ),
    sections: (
      <>
        <section id='my-role'>
          <h2>My Role</h2>
          <p>
            <strong className='reveal'>
              I led the end-to-end development of this project
            </strong>{' '}
            from initial requirement gathering and client communication to final
            delivery and post-launch maintenance.{' '}
            <strong className='reveal'>
              I was the primary point of contact for all{' '}
            </strong>
            technical discussions, collaborating closely with stakeholders
            across different phases to shape the product vision into a working
            solution.
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              UI/UX design and frontend development
            </li>
            <li>
              <Bullet />
              Direct client interaction for feedback loops
            </li>
            <li>
              <Bullet />
              Feature planning, estimation, and prioritization
            </li>
            <li>
              <Bullet />
              Integrations and deployment
            </li>
            <li>
              <Bullet />
              Ongoing bug fixes and improvements
            </li>
          </ul>
        </section>

        <section id='problem'>
          <h2>The Problem</h2>
          <p>
            The consultancy was managing client information, orders, and
            follow-ups manually across spreadsheets and communication platforms.
            This led to:
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Inefficient tracking of client progress and consultations
            </li>
            <li>
              <Bullet />
              <span className='reveal'>Delayed communication</span> of diet
              plans and updates
            </li>
            <li>
              <Bullet />
              No centralized system for viewing or analyzing client data
            </li>
            <li>
              <Bullet />
              Scattered workflows across nutritionists, order handlers, and
              administrators
            </li>
          </ul>
        </section>

        <section id='solution'>
          <h2>The Solution</h2>
          <p>We designed and developed a tailored CRM platform that:</p>
          <ul className='list'>
            <li>
              <Bullet />
              Centralized all client data in a secure, accessible system
            </li>
            <li>
              <Bullet />
              Automated order processing and diet plan distribution
            </li>
            <li>
              <Bullet />
              Enabled filtered views of clients by status, type, or assigned
              consultant
            </li>
            <li>
              <Bullet />
              Integrated email functionality to send rich HTML + PDF attachments
            </li>
            <li>
              <Bullet />
              Provided a scalable, modular structure to evolve with business
              needs
            </li>
          </ul>
        </section>

        <section id='my-work'>
          <h2>Modules I Developed</h2>
          <p>
            I contributed to and led development of multiple critical modules,
            including:
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Client Management: Add/edit client profiles, track status, assign
              consultants, search and filter across 1000+ entries
            </li>
            <li>
              <Bullet />
              Analytics Dashboard: Visual insights into orders, active clients,
              diet plan engagement
            </li>
            <li>
              <Bullet />
              To-Do Module: Task tracking for nutritionists and admins
            </li>
            <li>
              <Bullet />
              User Management: Admins could manage roles and access levels
            </li>
            {/*  */}
            <li>
              <Bullet />
              Orders Module: End-to-end order lifecycle with PDF and email
              support
            </li>
            <li>
              <Bullet />
              Questionnaire Module: Custom form responses for onboarding new
              clients
            </li>
            <li>
              <Bullet />
              Email Integration: Rich text email editor + automated PDF
              generation + attachments sent directly from the platform
            </li>
            <li>
              <Bullet />
              Calendly Integration: Easy consultation scheduling
            </li>
            <li>
              <Bullet />
              Special Notes & Recipes Module: Maintain client-specific insights
              and meal ideas
            </li>
            <li>
              <Bullet />
              Food Item Database: Master list of diet items for plan creation
            </li>
          </ul>
        </section>

        <section id='impact'>
          <h2 className='color font-size-title'>Impact</h2>
          <p>The solution delivered measurable operational improvements:</p>
          <ul className='list'>
            <li>
              <Bullet />
              30% reduction in manual errors related to order/diet dispatch
            </li>
            <li>
              <Bullet />
              3x faster diet plan delivery using automated email + PDF
              generation
            </li>
            <li>
              <Bullet />
              ~40% improvement in operational efficiency across client handling
            </li>
            <li>
              <Bullet />
              Enhanced client satisfaction due to faster communication and
              better data handling
            </li>
            <li>
              <Bullet />
              Improved collaboration between consultants through centralized
              notes and to-dos
            </li>
            <li>
              <Bullet />
              Seamless scaling to over 1000 clients with smooth search/filter UX
            </li>
          </ul>
        </section>
      </>
    ),
    images: [
      '/assets/projects/quality-track/1.png',
      '/assets/projects/quality-track/2.png',
      '/assets/projects/quality-track/3.png',
      '/assets/projects/quality-track/4.png',
      '/assets/projects/quality-track/5.png',
    ],
    techStack: [
      { src: NextJsIcon, alt: 'Next JS' },
      { src: ReactQueryIcon, alt: 'React Query' },
      { src: TypeScriptIcon, alt: 'Type script' },
      { src: MongoIcon, alt: 'Mongo DB' },
      { src: MuiIcon, alt: 'Material UI' },
    ],
  },
  {
    id: '03',
    title: 'Tour Touch CRM',
    date: '5th March 2024',
    overview: (
      <>
        <main className='color'>
          <section>
            <p>
              <span class='tooltip bold'>
                Tour Touch CRM
                <span class='tooltiptext'>
                  Original name has been changed to maintain confidentiality.
                </span>
              </span>{' '}
              is a comprehensive travel agency CRM built to streamline customer
              inquiries, itinerary planning, hotel bookings, and financial
              tracking.
            </p>
          </section>

          <section>
            <h2>Challenges in Traditional Travel Management:</h2>
            <p className='mb-10'>
              Travel agencies traditionally managed operations through
              spreadsheets, emails, and manual document handling, leading to
              errors, inefficiencies, and difficulties in customer retention.
              Tour Touch CRM provides a centralized, digital solution to handle
              everything from lead management to post-travel feedback.
            </p>
          </section>
        </main>
      </>
    ),
    sections: (
      <>
        <section id='objectives'>
          <h2 className='color font-size-title'>Background & Objectives</h2>
          <p>
            The goal was to simplify and digitize the workflow for travel
            agencies, enabling them to: - Manage customer inquiries efficiently
            - Generate itineraries with automated cost calculations - Handle
            hotel & vehicle bookings seamlessly - Track agent performance &
            revenue
          </p>
        </section>
        <section id='approach'>
          <h2>Approach</h2>
          <p>
            The development approach focused on automation, multi-user
            collaboration, and seamless data retrieval.
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Automating customer inquiries and follow-ups.
            </li>
            <li>
              <Bullet />
              Integrating real-time itinerary updates & cost estimations.
            </li>
            <li>
              <Bullet />
              Implementing multi-tenant support for different travel agencies.
            </li>
          </ul>
        </section>
        <section id='features'>
          <h2>Features</h2>
          <p className='bold reveal'>
            Lead & Inquiry Management: Centralized client communication
          </p>
          <p>
            All customer inquiries are logged and categorized for easy tracking.
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Convert inquiries into bookings in one click.
            </li>
            <li>
              <Bullet />
              Automated reminders for pending leads.
            </li>
          </ul>

          <p className='bold reveal'>
            Itinerary Planner: Auto-generates travel schedules
          </p>
          <p>
            Allows travel agents to create, edit, and send itineraries with
            real-time pricing.
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Auto-generates PDFs for clients.
            </li>
            <li>
              <Bullet />
              Calculates pricing based on accommodations, transport, and
              activities.
            </li>
          </ul>
        </section>
        <section id='impact'>
          <h2>Impact</h2>
          <ul className='list'>
            <li>
              <Bullet />
              40% faster itinerary generation.
            </li>
            <li>
              <Bullet />
              Improved customer retention with automated follow-ups.
            </li>
          </ul>
        </section>
      </>
    ),
    images: [
      '/assets/projects/travel-crm/1.png',
      '/assets/projects/travel-crm/2.png',
      '/assets/projects/travel-crm/3.png',
    ],
    techStack: [
      { src: NextJsIcon, alt: 'Next JS' },
      { src: NodeJsIcon, alt: 'Node JS' },
      { src: MongoIcon, alt: 'Mongo DB' },
      { src: TypeScriptIcon, alt: 'TypeScript' },
    ],
  },
  {
    id: '04',
    title: 'Shashvat Connect Mobile App',
    date: '10th April 2024',
    overview: (
      <>
        <main className='color'>
          <section>
            <p>
              <span class='tooltip bold'>
                Shashvat Connect
                <span class='tooltiptext'>
                  A community-driven mobile app for schools.
                </span>
              </span>{' '}
              is a React Native mobile app designed to bridge communication gaps
              between teachers, students, and parents.
            </p>
          </section>

          <section>
            <h2>Challenges in School Communication:</h2>
            <p className='mb-10'>
              Schools struggled with offline notices, fragmented communication,
              and lack of real-time updates. Shashvat Connect was designed to
              digitize school interactions, reduce paperwork, and improve
              student engagement.
            </p>
          </section>
        </main>
      </>
    ),
    sections: (
      <>
        <section id='objectives'>
          <h2 className='color font-size-title'>Background & Objectives</h2>
          <p>
            The primary objective was to build a seamless communication platform
            for schools, integrating: - Attendance tracking - Messaging &
            announcements - Schedules & homework assignments - Real-time
            notifications
          </p>
        </section>
        <section id='approach'>
          <h2>Approach</h2>
          <p>
            The development approach focused on mobile-first design, push
            notifications, and seamless data flow.
          </p>
          <ul className='list'>
            <li>
              <Bullet />
              Built with React Native for cross-platform support.
            </li>
            <li>
              <Bullet />
              Firebase integration for instant notifications.
            </li>
          </ul>
        </section>
        <section id='features'>
          <h2>Features</h2>
          <ul className='list'>
            <li>Live Attendance Tracking with check-in timestamps.</li>
            <li>Direct Messaging between parents and teachers.</li>
            <li>Automated Notifications for schedule changes.</li>
            <li>Event Management System for school activities.</li>
          </ul>
        </section>
        <section id='impact'>
          <h2>Impact</h2>
          <ul className='list'>
            <li>80% improvement in parent-teacher communication.</li>
            <li>Reduced paper usage by 70%.</li>
          </ul>
        </section>
      </>
    ),
    images: [
      '/assets/projects/shashvat/1.png',
      '/assets/projects/shashvat/2.png',
      '/assets/projects/shashvat/3.png',
    ],
    techStack: [
      { src: ReactNativeIcon, alt: 'React Native' },
      { src: TypeScriptIcon, alt: 'TypeScript' },
    ],
  },
];

export default projects;
